import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/App";
import { useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import ManufacturerCombobox from "@/pages/ManufacturerCombobox";
import { useBrandLogos, brandLogoFor } from "@/lib/brandLogos";
import ToolLogo from "@/components/ToolLogo";
import GroupedCompatibilityList from "@/components/GroupedCompatibilityList";
import NewBadge from "@/components/NewBadge";
import BoschLookup from "@/components/BoschLookup";

const PAGE_SIZE = 30;

function formatBytes(n) {
  if (!n && n !== 0) return "—";
  const units = ["B", "kB", "MB", "GB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(1)} ${units[i]}`;
}

// Deliberately no fuel/vehicle-type filters here (unlike the other 4
// providers) — this source has neither field, since each row is one
// original ECU dump file, not a vehicle-to-ECU compatibility mapping.
export default function DynoecuCompatibility() {
  const brandLogos = useBrandLogos();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get("search") || "";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(initialSearch);
  const [searchInput, setSearchInput] = useState(initialSearch);
  const [ecuMaker, setEcuMaker] = useState("all");
  const [brand, setBrand] = useState(null);
  const [facets, setFacets] = useState({
    ecu_makers: [],
    ecu_types: [],
    brands: [],
    total: 0,
  });

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (search) params.search = search;
      if (ecuMaker !== "all") params.ecu_maker = ecuMaker;
      if (brand) params.brand = brand;
      const { data } = await api.get("/dynoecu/vehicles", { params });
      setItems(data.items);
      setTotal(data.total);
      setTotalPages(data.total_pages);
    } catch (e) {
      toast.error("Fehler beim Laden der Original-ECU-Dateien-Liste");
    } finally {
      setLoading(false);
    }
  }, [page, search, ecuMaker, brand]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dynoecu/facets");
        setFacets(data);
      } catch (err) {
        console.warn("facets error", err);
      }
    })();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
      if (searchInput) {
        setSearchParams({ search: searchInput }, { replace: true });
      } else if (searchParams.get("search")) {
        setSearchParams({}, { replace: true });
      }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const showingRange = useMemo(() => {
    if (total === 0) return "0";
    const from = (page - 1) * PAGE_SIZE + 1;
    const to = Math.min(page * PAGE_SIZE, total);
    return `${from}–${to}`;
  }, [page, total]);

  return (
    <div data-testid="dynoecu-page" className="space-y-6">
      {/* Hero */}
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <ToolLogo source="dynoecu" />
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#D32222]">
              Original ECU Dateien · Dyno-ChiptuningFiles Mirror
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 text-center">
          <StatCard
            label="Dateien"
            value={facets.total.toLocaleString("de-DE")}
            testid="dynoecu-stat-total"
          />
          <StatCard
            label="Marken"
            value={facets.brands?.length ?? 0}
            testid="dynoecu-stat-brands"
          />
          <StatCard
            label="ECU-Hersteller"
            value={facets.ecu_makers?.length ?? 0}
            testid="dynoecu-stat-ecu-makers"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-neutral-800 bg-panel p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <Input
            data-testid="dynoecu-search-input"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Suche Marke, Modell, Hardware-/Software-Nr., ECU-Typ…"
            className="pl-9 bg-panel border-neutral-800 text-neutral-100 placeholder:text-neutral-500 focus-visible:ring-[#D32222]"
          />
        </div>
        <FilterSelect
          testid="dynoecu-filter-ecu-maker"
          value={ecuMaker}
          onChange={(v) => {
            setEcuMaker(v);
            setPage(1);
          }}
          placeholder="ECU-Hersteller"
          options={[
            { value: "all", label: "Alle Hersteller" },
            ...(facets.ecu_makers || []).map((v) => ({ value: v, label: v })),
          ]}
        />
        <ManufacturerCombobox
          value={brand}
          onChange={(v) => {
            setBrand(v);
            setPage(1);
          }}
          options={facets.brands || []}
          placeholder="Alle Marken"
          testid="dynoecu-brand-combobox"
        />
      </div>

      <GroupedCompatibilityList
        provider="dynoecu"
        params={{ search: search || undefined, ecu_maker: ecuMaker !== "all" ? ecuMaker : undefined, brand: brand || undefined }}
        renderRow={(row) => <Row row={row} brandLogos={brandLogos} grouped />}
        emptyText="Keine Einträge gefunden. Passe deine Filter an oder starte den Original-ECU-Dateien-Scraper."
      />

      {/* Legacy flat table kept hidden for compatibility */}
      <div className="hidden rounded-xl border border-neutral-800 bg-panel overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-4 px-4 pt-3 pb-2 text-[11px] uppercase tracking-[0.2em] text-neutral-500 border-b border-neutral-800">
          <div className="col-span-3">Marke / Modell</div>
          <div className="col-span-2">Motor</div>
          <div className="col-span-2">Hardware / Software</div>
          <div className="col-span-2">ECU</div>
          <div className="col-span-1">Größe</div>
          <div className="col-span-2">Auslesbar mit</div>
        </div>

        <div data-testid="dynoecu-list" className="divide-y divide-neutral-800/70">
          {loading && items.length === 0 ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="px-4 py-4 animate-pulse grid grid-cols-12 gap-4">
                <div className="col-span-3 h-6 bg-neutral-900 rounded" />
                <div className="col-span-2 h-6 bg-neutral-900 rounded" />
                <div className="col-span-2 h-6 bg-neutral-900 rounded" />
                <div className="col-span-2 h-6 bg-neutral-900 rounded" />
                <div className="col-span-1 h-6 bg-neutral-900 rounded" />
                <div className="col-span-2 h-6 bg-neutral-900 rounded" />
              </div>
            ))
          ) : items.length === 0 ? (
            <div
              data-testid="dynoecu-empty-state"
              className="px-4 py-16 text-center text-neutral-500"
            >
              Keine Einträge gefunden. Passe deine Filter an oder starte den
              Original-ECU-Dateien-Scraper.
            </div>
          ) : (
            items.map((row, idx) => <Row key={idx} row={row} brandLogos={brandLogos} />)
          )}
        </div>
      </div>

      {/* Pagination */}
      <div className="hidden flex-wrap items-center justify-between gap-4 pt-2">
        <div className="text-sm text-neutral-500">
          Zeige{" "}
          <span data-testid="dynoecu-range-label" className="text-neutral-200">
            {showingRange}
          </span>{" "}
          von{" "}
          <span data-testid="dynoecu-total-label" className="text-neutral-200">
            {total.toLocaleString("de-DE")}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="dynoecu-prev-page"
            variant="outline"
            className="border-neutral-700 bg-panel hover:bg-neutral-800 text-neutral-100"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Zurück
          </Button>
          <div className="text-sm text-neutral-400 px-2">
            Seite <span className="text-neutral-100">{page}</span> /{" "}
            <span className="text-neutral-100">
              {totalPages.toLocaleString("de-DE")}
            </span>
          </div>
          <Button
            data-testid="dynoecu-next-page"
            variant="outline"
            className="border-neutral-700 bg-panel hover:bg-neutral-800 text-neutral-100"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || loading}
          >
            Weiter <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-lg border border-neutral-800 bg-panel px-4 py-3 min-w-[110px]"
    >
      <div className="text-xl font-semibold text-[#D32222] tabular-nums">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mt-1">
        {label}
      </div>
    </div>
  );
}

function FilterSelect({ testid, value, onChange, placeholder, options }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        data-testid={testid}
        className="w-[190px] h-auto min-h-9 py-1.5 bg-panel border-neutral-800 text-neutral-100 focus:ring-[#D32222]"
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="bg-panel border-neutral-800 text-neutral-100">
        {options.map((o) => (
          <SelectItem
            key={o.value}
            value={o.value}
            data-testid={`${testid}-opt-${o.value}`}
            className="focus:bg-neutral-800"
          >
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function Row({ row, brandLogos, grouped = false }) {
  const logo = brandLogoFor(brandLogos, row.brand);

  return (
    <div
      data-testid="dynoecu-row"
      className="group grid grid-cols-1 md:grid-cols-12 gap-4 px-4 py-4 transition"
    >
      {/* Marke / Modell */}
      <div className={grouped ? "hidden" : "md:col-span-3 flex items-center gap-3"}>
        {logo && (
          <img
            src={logo}
            alt={row.brand}
            className="h-16 w-16 object-contain rounded bg-panel border border-neutral-800 p-1.5 shrink-0"
            loading="lazy"
          />
        )}
        <div>
          <div className="font-medium text-neutral-100">{row.brand || "—"}</div>
          <div className="text-xs text-neutral-500">{row.model || "—"}</div>
        </div>
      </div>
      {/* Motor */}
      <div className="md:col-span-2 text-neutral-200">
        {row.engine || "—"}
        <div><NewBadge firstSeen={row.first_seen} className="mt-1" /></div>
      </div>
      {/* Hardware / Software */}
      <div className="md:col-span-2">
        <div
          className="flex items-center gap-1.5 text-neutral-100"
          style={{ fontFamily: "'JetBrains Mono', monospace" }}
        >
          {row.hardware || "—"}
          <BoschLookup hardware={row.hardware} />
        </div>
        <div className="text-[11px] text-neutral-500 mt-0.5">
          {row.software ? `SW ${row.software}` : ""}
        </div>
      </div>
      {/* ECU */}
      <div className="md:col-span-2">
        <div className="text-neutral-100">{row.ecu_type || "—"}</div>
        <div className="text-[11px] text-neutral-400 mt-0.5">{row.ecu_maker || ""}</div>
      </div>
      {/* Größe */}
      <div className="md:col-span-1 text-neutral-300 text-sm">{formatBytes(row.size)}</div>
      {/* Auslesbar mit */}
      <div className="md:col-span-2 flex flex-wrap gap-1.5 items-start">
        {row.read_hardware ? (
          <Badge
            variant="outline"
            className="border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[10px]"
          >
            {row.read_hardware}
          </Badge>
        ) : (
          <span className="text-neutral-600 text-sm">—</span>
        )}
      </div>
    </div>
  );
}
